import { useState, useEffect } from "react";
import config from "../config";
import SocialIcon from "../components/SocialIcon";

const { API_BASE, UPLOADS_URL } = config;

async function fetchJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status} – ${url}`);
  return res.json();
}

const TOP_LEVEL_ROUTES = new Set([
  "portfolio", "products", "blog", "testimonials",
  "about-us", "contact-us",
]);

function resolveLink(link = "") {
  if (link.startsWith("/")) return link;
  if (TOP_LEVEL_ROUTES.has(link)) return `/${link}`;
  return `/services/${link}`;
}

const ChevronIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2 5h6M5 2l3 3-3 3" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WhatsappGlyph = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
    <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521h-.003a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.432-.156-.25a6.56 6.56 0 0 1-1.007-3.503c0-3.626 2.957-6.578 6.594-6.578a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.002 3.627-2.957 6.582-6.592 6.582zm3.615-4.934c-.198-.099-1.17-.578-1.353-.644-.182-.066-.314-.099-.446.099-.132.198-.512.644-.628.776-.115.132-.231.149-.43.05-.198-.1-.835-.308-1.59-.982-.588-.524-.985-1.171-1.1-1.369-.116-.198-.012-.305.087-.405.09-.089.198-.232.297-.347.1-.116.133-.198.198-.331.066-.132.033-.247-.016-.347-.05-.099-.446-1.076-.611-1.473-.161-.387-.325-.335-.446-.341-.115-.006-.247-.007-.379-.007-.132 0-.347.05-.529.247-.182.198-.694.677-.694 1.654 0 .976.71 1.92.81 2.052.099.132 1.394 2.127 3.379 2.984.472.204.84.325 1.127.416.473.15.904.129 1.244.078.38-.057 1.171-.479 1.336-.942.165-.462.165-.858.116-.941-.05-.083-.182-.132-.38-.231z"/>
  </svg>
);

const PaperPlaneIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M14.5 1.5 1 7.2l4.6 1.8M14.5 1.5 9.6 14.5l-4-5.5M14.5 1.5 5.6 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 3l10 10M13 3 3 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const JOB_TYPES = ["New Install", "Repair", "Regas", "Maintenance", "Inspection", "Other"];
const PROPERTY_TYPES = ["Residential", "Commercial", "Industrial", "Dehumidifier Sales", "Other"];

function getBusinessStatus() {
  const now = new Date();
  const sastMinutes = ((now.getUTCHours() * 60 + now.getUTCMinutes()) + 120) % 1440;
  const openMinutes  = 7 * 60;
  const closeMinutes = 17 * 60;
  const isOpen = sastMinutes >= openMinutes && sastMinutes < closeMinutes;
  const isClosingSoon = isOpen && (closeMinutes - sastMinutes) <= 60;
  return {
    label: !isOpen ? "Closed now" : isClosingSoon ? "Closing soon" : "Open now",
    dotClass: !isOpen ? "ft-widget__dot--closed" : "ft-widget__dot--open",
  };
}

function EnquiryForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    job_type: "", property_type: "",
    preferred_date: "", service_address: "",
    subject: "", message: "",
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [statusMsg, setStatusMsg] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrors({});
    setStatusMsg("");

    try {
      const res = await fetch(`${API_BASE}/contact-submit.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setStatus("success");
        setStatusMsg(
          data.email_sent
            ? "Your booking request has been received. We'll be in touch shortly to confirm."
            : "Your booking request has been saved. We'll be in touch shortly to confirm."
        );
        setForm({
          name: "", email: "", phone: "",
          job_type: "", property_type: "",
          preferred_date: "", service_address: "",
          subject: "", message: "",
          website: "",
        });
      } else if (data.errors) {
        setErrors(data.errors);
        setStatus("error");
        setStatusMsg("Please fix the errors below.");
      } else {
        setStatus("error");
        setStatusMsg(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Footer enquiry form submit error:", err);
      setStatus("error");
      setStatusMsg("Could not send your request. Please try again later.");
    }
  };

  const isSending = status === "sending";

  if (status === "success") {
    return (
      <div className="ft-widget__success">
        <div className="ft-widget__success-icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <p className="ft-widget__success-msg">{statusMsg}</p>
        <button type="button" className="ft-widget__success-btn" onClick={() => setStatus("idle")}>
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form className="ft-widget__form" onSubmit={handleSubmit} noValidate>
      <input
        type="text" name="website" value={form.website} onChange={handleChange}
        className="ft-widget__honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true"
      />

      <div className="ft-widget__row">
        <div className="ft-widget__field">
          <label htmlFor="ftw-name">Full Name *</label>
          <input
            id="ftw-name" name="name" type="text"
            value={form.name} onChange={handleChange}
            className={errors.name ? "ft-widget__input--error" : ""}
            placeholder="John Doe"
          />
          {errors.name && <span className="ft-widget__error">{errors.name}</span>}
        </div>
        <div className="ft-widget__field">
          <label htmlFor="ftw-email">Email *</label>
          <input
            id="ftw-email" name="email" type="email"
            value={form.email} onChange={handleChange}
            className={errors.email ? "ft-widget__input--error" : ""}
            placeholder="john@example.com"
          />
          {errors.email && <span className="ft-widget__error">{errors.email}</span>}
        </div>
      </div>

      <div className="ft-widget__field">
        <label htmlFor="ftw-phone">Phone Number</label>
        <input
          id="ftw-phone" name="phone" type="tel"
          value={form.phone} onChange={handleChange}
          placeholder="+27 73 932 6464"
        />
      </div>

      <div className="ft-widget__row">
        <div className="ft-widget__field">
          <label htmlFor="ftw-job-type">Service Required *</label>
          <select
            id="ftw-job-type" name="job_type"
            value={form.job_type} onChange={handleChange}
            className={errors.job_type ? "ft-widget__input--error" : ""}
          >
            <option value="">Select…</option>
            {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {errors.job_type && <span className="ft-widget__error">{errors.job_type}</span>}
        </div>
        <div className="ft-widget__field">
          <label htmlFor="ftw-property-type">Property Type *</label>
          <select
            id="ftw-property-type" name="property_type"
            value={form.property_type} onChange={handleChange}
            className={errors.property_type ? "ft-widget__input--error" : ""}
          >
            <option value="">Select…</option>
            {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {errors.property_type && <span className="ft-widget__error">{errors.property_type}</span>}
        </div>
      </div>

      <div className="ft-widget__field">
        <label htmlFor="ftw-message">Tell Us More *</label>
        <textarea
          id="ftw-message" name="message" rows={3}
          value={form.message} onChange={handleChange}
          className={errors.message ? "ft-widget__input--error" : ""}
          placeholder="Describe the issue and any access details..."
        />
        {errors.message && <span className="ft-widget__error">{errors.message}</span>}
      </div>

      {statusMsg && status === "error" && (
        <div className="ft-widget__status ft-widget__status--error">{statusMsg}</div>
      )}

      <button type="submit" className="ft-widget__submit" disabled={isSending}>
        {isSending ? "Submitting…" : "Send Enquiry"}
        {!isSending && <PaperPlaneIcon size={14} />}
      </button>
    </form>
  );
}

function WhatsappPanel({ phoneNumber }) {
  const digits = (phoneNumber || "").replace(/[^\d]/g, "");
  const waLink = digits ? `https://wa.me/${digits}` : null;

  return (
    <div className="ft-widget__wa">
      <div className="ft-widget__wa-icon">
        <WhatsappGlyph size={34} />
      </div>
      <h4 className="ft-widget__wa-title">Chat via WhatsApp</h4>
      <p className="ft-widget__wa-desc">
        Connect with our team directly on WhatsApp for a quick conversation about your AC service needs.
      </p>
      {waLink ? (
        <a href={waLink} target="_blank" rel="noopener noreferrer" className="ft-widget__wa-btn">
          <WhatsappGlyph size={16} /> Open WhatsApp
        </a>
      ) : (
        <span className="ft-widget__wa-unavailable">WhatsApp number unavailable</span>
      )}
    </div>
  );
}

function ContactWidget({ contact }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("whatsapp");
  const [status, setStatus] = useState(() => getBusinessStatus());

  useEffect(() => {
    const id = setInterval(() => setStatus(getBusinessStatus()), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
    if (open) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  return (
    <div className="ft-widget">
      {open && (
        <div className="ft-widget__panel" role="dialog" aria-modal="true" aria-label="Get in touch">
          <div className="ft-widget__header">
            <div className="ft-widget__header-top">
              <span className="ft-widget__header-title">Get In Touch</span>
              <button
                type="button"
                className="ft-widget__close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <CloseIcon size={16} />
              </button>
            </div>
            <div className="ft-widget__status-row">
              <span className={`ft-widget__dot ${status.dotClass}`} aria-hidden="true" />
              {status.label} • 07:00–17:00 SAST
            </div>
          </div>

          <div className="ft-widget__tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "whatsapp"}
              className={`ft-widget__tab ${tab === "whatsapp" ? "ft-widget__tab--active" : ""}`}
              onClick={() => setTab("whatsapp")}
            >
              <WhatsappGlyph size={14} /> WhatsApp
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "enquiry"}
              className={`ft-widget__tab ${tab === "enquiry" ? "ft-widget__tab--active" : ""}`}
              onClick={() => setTab("enquiry")}
            >
              <PaperPlaneIcon size={14} /> Enquiry Form
            </button>
          </div>

          <div className="ft-widget__body">
            {tab === "whatsapp" ? (
              <WhatsappPanel phoneNumber={contact?.phone_number} />
            ) : (
              <EnquiryForm />
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        className="ft-widget__fab"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Close contact panel" : "Open contact panel"}
      >
        <span className={`ft-widget__dot ${status.dotClass}`} aria-hidden="true" />
        BOOK NOW
      </button>
    </div>
  );
}

function Footer() {
  const [logo,      setLogo]      = useState(null);
  const [tree,      setTree]      = useState([]);
  const [social,    setSocial]    = useState([]);
  const [contact,   setContact]   = useState(null);
  const [aboutUs,   setAboutUs]   = useState(null);
  const [bandImage, setBandImage] = useState(null);

  /* ── Newsletter subscribe form state ── */
  const [nlEmail,  setNlEmail]  = useState("");
  const [nlStatus, setNlStatus] = useState("idle"); // idle | sending | success | error
  const [nlMsg,    setNlMsg]    = useState("");

  useEffect(() => {
    Promise.all([
      fetchJSON(`${API_BASE}/logo.php`),
      fetchJSON(`${API_BASE}/navbar.php`),
      fetchJSON(`${API_BASE}/social-links.php`),
      fetchJSON(`${API_BASE}/company-contact.php`),
    ])
      .then(([logoData, navData, socialData, contactData]) => {
        setLogo(logoData);

        const all = Array.isArray(navData)
          ? navData
          : (navData?.items || navData?.menu || navData?.data || []);

        setBandImage(navData?.image1 || null);

        const roots = all
          .filter((n) => !n.parent_id || n.parent_id === 0)
          .sort((a, b) => a.order - b.order)
          .map((root) => ({
            ...root,
            children: all
              .filter((c) => String(c.parent_id) === String(root.id))
              .sort((a, b) => a.order - b.order),
          }));
        setTree(roots);
        setSocial(Array.isArray(socialData) ? socialData : []);
        setContact(contactData);
      })
      .catch((err) => console.error("Footer load error:", err));

    fetchJSON(`${API_BASE}/about.php`).then(setAboutUs).catch(() => {});
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (nlStatus === "sending") return;

    setNlStatus("sending");
    setNlMsg("");

    try {
      const res = await fetch(`${API_BASE}/newsletter-subscribe.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: nlEmail }),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setNlStatus("success");
        setNlMsg(data.message || "Thanks for subscribing!");
        setNlEmail("");
      } else if (data.errors) {
        setNlStatus("error");
        setNlMsg(data.errors.email || "Please enter a valid email address.");
      } else {
        setNlStatus("error");
        setNlMsg(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("Newsletter subscribe error:", err);
      setNlStatus("error");
      setNlMsg("Could not subscribe. Please try again later.");
    }
  };

  const year = new Date().getFullYear();

  const parentsWithChildren = tree.filter((item) => item.children?.length > 0);

  /* Only the first 2 parent items (service categories) get their own column */
  const sections = parentsWithChildren.slice(0, 2);

  /* Root items with no children → base quick links */
  const quickLinks = tree.filter((item) => !item.children?.length);

  /* Child items from ALL parents whose link is a top-level route
     (portfolio, products, blog, testimonials, etc.) */
  const portfolioLinks = parentsWithChildren
    .flatMap((parent) => parent.children)
    .filter((child) => TOP_LEVEL_ROUTES.has(child.link));

  return (
    <footer className="ft">

      {/* ── TOP NEWSLETTER BAND ── */}
      <div
        className="ft__band"
        style={bandImage ? { backgroundImage: `url(${UPLOADS_URL}/navbar/${bandImage})` } : undefined}
      >
        <div className="ft__band-inner">
          <div className="ft__band-text">
            <span className="ft__band-eyebrow">Stay In The Loop</span>
            <h2 className="ft__band-heading">
              Subscribe To Our <em>Newsletter</em>
            </h2>
          </div>

          <form className="ft__newsletter" onSubmit={handleNewsletterSubmit} noValidate>
            {nlStatus === "success" ? (
              <p className="ft__newsletter-msg ft__newsletter-msg--success">{nlMsg}</p>
            ) : (
              <>
                <div className="ft__newsletter-row">
                  <input
                    type="email"
                    name="newsletter_email"
                    value={nlEmail}
                    onChange={(e) => setNlEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="ft__newsletter-input"
                    disabled={nlStatus === "sending"}
                    aria-label="Email address"
                    required
                  />
                  <button type="submit" className="ft__newsletter-btn" disabled={nlStatus === "sending"}>
                    {nlStatus === "sending" ? "Subscribing…" : "Subscribe"}
                    {nlStatus !== "sending" && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                      </svg>
                    )}
                  </button>
                </div>
                {nlMsg && nlStatus === "error" && (
                  <p className="ft__newsletter-msg ft__newsletter-msg--error">{nlMsg}</p>
                )}
              </>
            )}
          </form>
        </div>
      </div>

      {/* ── MAIN BODY ── */}
      <div className="ft__body">
        <div className="ft__inner">

          {/* Col — Brand */}
          <div className="ft__col ft__col--brand">
            {logo ? (
              <a href="/" className="ft__logo" aria-label="Homepage">
                <img
                  src={`${UPLOADS_URL}/logos/${logo.image_filename}`}
                  alt={logo.alt_text || logo.title || "Logo"}
                  className="ft__logo-img"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </a>
            ) : (
              <a href="/" className="ft__logo ft__logo--text">Vybertech</a>
            )}

            <p className="ft__about">
              {aboutUs?.content
                ? aboutUs.content.substring(0, 180) + (aboutUs.content.length > 180 ? "…" : "")
                : "We are a professional company dedicated to delivering quality AC installation, repairs, and maintenance services."}
            </p>

            {social.length > 0 && (
              <div className="ft__social">
                {social.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ft__soc-link"
                    aria-label={s.platform}
                  >
                    <SocialIcon platform={s.platform} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* QUICK LINKS — root items with no children + top-level portfolio links */}
          {(quickLinks.length > 0 || portfolioLinks.length > 0) && (
            <div className="ft__col">
              <h3 className="ft__col-heading">Quick Links</h3>
              <ul className="ft__links">
                {quickLinks.map((item) => (
                  <li key={item.id}>
                    <a href={resolveLink(item.link)} className="ft__link">
                      <ChevronIcon />
                      {item.label}
                    </a>
                  </li>
                ))}
                {portfolioLinks.map((item) => (
                  <li key={`pl-${item.id}`}>
                    <a href={resolveLink(item.link)} className="ft__link">
                      <ChevronIcon />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* One column per service parent that has children */}
          {sections.map((parent) => (
            <div key={parent.id} className="ft__col">
              <h3 className="ft__col-heading">{parent.label}</h3>
              <ul className="ft__links">
                {parent.children
                  .filter((child) => !TOP_LEVEL_ROUTES.has(child.link))
                  .map((child) => (
                    <li key={child.id}>
                      <a href={resolveLink(child.link)} className="ft__link">
                        <ChevronIcon />
                        {child.label}
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          ))}

          {/* Col — Contact */}
          {contact && (
            <div className="ft__col">
              <h3 className="ft__col-heading">Contact Us</h3>
              <ul className="ft__contact">

                {contact.phone_number && (
                  <li className="ft__contact-item">
                    <span className="ft__contact-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122l-2.19.547a1.745 1.745 0 0 1-1.657-.459L5.482 8.062a1.745 1.745 0 0 1-.46-1.657l.548-2.19a.678.678 0 0 0-.122-.58L3.654 1.328z"/>
                      </svg>
                    </span>
                    <div>
                      <span className="ft__contact-label">Phone</span>
                      <a href={`tel:${contact.phone_number}`} className="ft__contact-value">
                        {contact.phone_number}
                      </a>
                    </div>
                  </li>
                )}

                {contact.email_address && (
                  <li className="ft__contact-item">
                    <span className="ft__contact-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z"/>
                      </svg>
                    </span>
                    <div>
                      <span className="ft__contact-label">Email</span>
                      <a href={`mailto:${contact.email_address}`} className="ft__contact-value">
                        {contact.email_address}
                      </a>
                    </div>
                  </li>
                )}

                {contact.physical_address && (
                  <li className="ft__contact-item">
                    <span className="ft__contact-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
                      </svg>
                    </span>
                    <div>
                      <span className="ft__contact-label">Address</span>
                      <span className="ft__contact-value ft__contact-value--plain">
                        {contact.physical_address}
                      </span>
                    </div>
                  </li>
                )}

                {contact.business_hours && (
                  <li className="ft__contact-item">
                    <span className="ft__contact-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                      </svg>
                    </span>
                    <div>
                      <span className="ft__contact-label">Hours</span>
                      <span className="ft__contact-value ft__contact-value--plain">
                        {contact.business_hours}
                      </span>
                    </div>
                  </li>
                )}

              </ul>
            </div>
          )}

        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div className="ft__bottom">
        <div className="ft__bottom-inner">
          <div className="ft__bottom-left">
            <span className="ft__copy">
              © {year} {logo?.title || "Vybertech"}. All rights reserved.
            </span>
            <span className="ft__credit">
              Designed &amp; Developed by <span className="ft__credit-name">CodeCrue</span>
            </span>
          </div>
          <div className="ft__bottom-links">
            {aboutUs?.company_registration_number && (
              <>
                <span className="ft__reg-pill">
                  <span className="ft__reg-pill-label">Reg No.</span>
                  <span className="ft__reg-pill-value">{aboutUs.company_registration_number}</span>
                </span>
                <span className="ft__bottom-sep" aria-hidden="true">·</span>
              </>
            )}
            <span className="ft__bottom-link">Company Reg No.</span>
            <span className="ft__bottom-link">2020/889047/07</span>
          </div>
        </div>
      </div>

      {/* ── FLOATING CONTACT WIDGET ── */}
      {contact && <ContactWidget contact={contact} />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800&display=swap');

        .ft *, .ft *::before, .ft *::after { box-sizing: border-box; margin: 0; padding: 0; }
        .ft a { text-decoration: none; }
        .ft ul { list-style: none; }
        .ft { font-family: 'Roboto', sans-serif; }

        .ft__band {
          position: relative;
          background-color: #013d97;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          background-attachment: fixed;
          padding: 90px 0;
          overflow: hidden;
          isolation: isolate;
        }

        .ft__band-inner {
          position: relative;
          z-index: 1;
          max-width: 1280px; margin: 0 auto; padding: 0 40px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 24px; flex-wrap: wrap;
        }
        .ft__band-eyebrow {
          display: block; font-size: 12px; font-weight: 700;
          color: rgba(255,255,255,.75); letter-spacing: .1em;
          text-transform: uppercase; margin-bottom: 6px;
        }
        .ft__band-heading { font-size: clamp(18px, 2vw, 26px); font-weight: 800; color: #fff; line-height: 1.2; }
        .ft__band-heading em { font-style: normal; color: #6fc6f2; }

        /* ── Newsletter subscribe form (top band) ── */
        .ft__newsletter { flex-shrink: 0; width: 100%; max-width: 420px; }
        .ft__newsletter-row {
          display: flex; align-items: stretch; gap: 0;
          background: #fff; border-radius: 6px; overflow: hidden;
          box-shadow: 0 8px 24px rgba(0,0,0,.18);
        }
        .ft__newsletter-input {
          flex: 1; min-width: 0; border: none; outline: none;
          font-family: 'Roboto', sans-serif; font-size: 14px; color: #1a2236;
          padding: 14px 16px; background: transparent;
        }
        .ft__newsletter-input::placeholder { color: #9ca3af; }
        .ft__newsletter-input:disabled { background: #f3f4f6; }
        .ft__newsletter-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 14px 26px; background: #013d97; color: #fff;
          border: none; cursor: pointer;
          font-size: 14px; font-weight: 800; letter-spacing: .06em;
          text-transform: uppercase; white-space: nowrap; flex-shrink: 0;
          transition: background .2s;
        }
        .ft__newsletter-btn:hover:not(:disabled) { background: #012a6b; }
        .ft__newsletter-btn:disabled { opacity: .7; cursor: not-allowed; }
        .ft__newsletter-msg {
          margin-top: 10px; font-size: 13px; font-weight: 600;
        }
        .ft__newsletter-msg--success { color: #6fe89a; }
        .ft__newsletter-msg--error { color: #ff9a9a; }

        .ft__body { background: #011f52; padding: 64px 0 48px; }
        .ft__inner {
          max-width: 1280px; margin: 0 auto; padding: 0 40px;
          display: flex; flex-wrap: wrap;
          gap: 48px; align-items: start;
        }

        .ft__col-heading {
          font-size: 15px; font-weight: 800; color: #fff;
          letter-spacing: .06em; text-transform: uppercase;
          margin-bottom: 22px; padding-bottom: 12px; position: relative;
        }
        .ft__col-heading::after {
          content: ""; position: absolute; bottom: 0; left: 0;
          width: 32px; height: 3px; background: #029add;
        }

        .ft__col { flex: 1 1 160px; min-width: 160px; }
        .ft__col--brand { flex: 1.5 1 280px; min-width: 280px; }
        .ft__col:last-child { flex: 1.2 1 240px; min-width: 240px; }

        .ft__logo {
          display: inline-block; margin-bottom: 20px;
          background: #fff; border-radius: 12px; padding: 10px;
        }
        .ft__logo-img { height: 72px; width: auto; object-fit: contain; display: block; }
        .ft__logo--text {
          font-size: 24px; font-weight: 800; color: #013d97;
          background: #fff; border-radius: 12px; padding: 10px 20px;
        }
        .ft__about {
          font-size: 14px; color: rgba(255,255,255,.65);
          line-height: 1.75; margin-bottom: 24px; max-width: 340px;
          text-align: justify;
        }
        .ft__social { display: flex; gap: 10px; flex-wrap: wrap; }
        .ft__soc-link {
          width: 38px; height: 38px; border-radius: 6px;
          background: rgba(255,255,255,.1); display: flex;
          align-items: center; justify-content: center; color: #fff;
          transition: background .2s, transform .15s;
        }
        .ft__soc-link:hover { background: #029add; transform: translateY(-2px); }

        .ft__links { display: flex; flex-direction: column; gap: 4px; }
        .ft__link {
          display: flex; align-items: center; gap: 8px; padding: 6px 0;
          font-size: 14px; font-weight: 500; color: rgba(255,255,255,.7);
          border-bottom: 1px solid rgba(255,255,255,.06);
          transition: color .2s, gap .2s;
        }
        .ft__link:hover { color: #029add; gap: 12px; }
        .ft__link svg { flex-shrink: 0; opacity: .6; transition: opacity .2s; }
        .ft__link:hover svg { opacity: 1; }

        .ft__contact { display: flex; flex-direction: column; gap: 20px; }
        .ft__contact-item { display: flex; align-items: flex-start; gap: 14px; }
        .ft__contact-icon {
          width: 36px; height: 36px; border-radius: 6px;
          background: rgba(2,154,221,.2); display: flex;
          align-items: center; justify-content: center;
          color: #029add; flex-shrink: 0;
        }
        .ft__contact-label {
          display: block; font-size: 11px; font-weight: 700; color: #029add;
          text-transform: uppercase; letter-spacing: .08em; margin-bottom: 3px;
        }
        .ft__contact-value {
          display: block; font-size: 13.5px; font-weight: 500;
          color: rgba(255,255,255,.8); transition: color .2s;
        }
        a.ft__contact-value:hover { color: #029add; }
        .ft__contact-value--plain { color: rgba(255,255,255,.65); }

        .ft__bottom { background: #010f2e; padding: 18px 0; }
        .ft__bottom-inner {
          max-width: 1280px; margin: 0 auto; padding: 0 40px;
          display: flex; align-items: center; justify-content: space-between;
          gap: 16px; flex-wrap: wrap;
        }
        .ft__bottom-left { display: flex; flex-direction: column; gap: 3px; }
        .ft__copy { font-size: 13px; color: rgba(255,255,255,.45); }
        .ft__credit { font-size: 12px; color: rgba(255,255,255,.32); }
        .ft__credit-name { color: rgba(255,255,255,.5); font-weight: 700; }

        .ft__bottom-links { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .ft__bottom-link { font-size: 13px; color: rgba(255,255,255,.45); transition: color .2s; }
        .ft__bottom-link:hover { color: #029add; }
        .ft__bottom-sep { color: rgba(255,255,255,.25); }

        .ft__reg-pill {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 20px;
          padding: 5px 12px;
        }
        .ft__reg-pill-label {
          font-size: 10px; font-weight: 800; letter-spacing: .06em;
          text-transform: uppercase; color: #029add;
        }
        .ft__reg-pill-value {
          font-size: 12px; font-weight: 600; color: rgba(255,255,255,.7);
        }

        /* ── FLOATING CONTACT WIDGET ── */
        .ft-widget {
          position: fixed; right: 24px; bottom: 24px; z-index: 2000;
          display: flex; flex-direction: column; align-items: flex-end; gap: 14px;
          font-family: 'Roboto', sans-serif;
        }

        .ft-widget__fab {
          display: inline-flex; align-items: center; gap: 10px;
          background: linear-gradient(120deg, #6a1fc4 0%, #b32fc9 100%);
          color: #fff; border: none; cursor: pointer;
          font-size: 14.5px; font-weight: 800; letter-spacing: .02em;
          padding: 15px 26px; border-radius: 50px;
          box-shadow: 0 10px 30px rgba(106,31,196,.4);
          transition: transform .2s, box-shadow .2s;
        }
        .ft-widget__fab:hover { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(106,31,196,.5); }

        .ft-widget__dot {
          width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0;
        }
        .ft-widget__dot--open { background: #00e741; box-shadow: 0 0 0 3px rgba(255,210,63,.3); }
        .ft-widget__dot--closed { background: #ec172c; box-shadow: 0 0 0 3px rgba(154,160,173,.3); }

        .ft-widget__panel {
          width: min(360px, calc(100vw - 32px));
          max-height: min(620px, calc(100vh - 120px));
          background: #14132b;
          border-radius: 16px;
          overflow: hidden;
          display: flex; flex-direction: column;
          box-shadow: 0 24px 64px rgba(0,0,0,.4);
          animation: ft-widget-in .22s cubic-bezier(.34,1.56,.64,1);
        }
        @keyframes ft-widget-in {
          from { opacity: 0; transform: translateY(16px) scale(.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }

        .ft-widget__header {
          background: linear-gradient(120deg, #6a1fc4 0%, #b32fc9 100%);
          padding: 20px 22px 16px;
          flex-shrink: 0;
        }
        .ft-widget__header-top {
          display: flex; align-items: center; justify-content: space-between;
          margin-bottom: 8px;
        }
        .ft-widget__header-title {
          font-size: 16px; font-weight: 800; color: #fff; letter-spacing: .02em;
        }
        .ft-widget__close {
          width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
          background: rgba(255,255,255,.18); border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center; color: #fff;
          transition: background .2s;
        }
        .ft-widget__close:hover { background: rgba(255,255,255,.3); }
        .ft-widget__status-row {
          display: flex; align-items: center; gap: 8px;
          font-size: 12.5px; font-weight: 600; color: rgba(255,255,255,.92);
        }

        .ft-widget__tabs { display: flex; flex-shrink: 0; background: #1b1a35; }
        .ft-widget__tab {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
          background: none; border: none; cursor: pointer;
          padding: 14px 10px;
          font-size: 12px; font-weight: 800; letter-spacing: .04em;
          color: rgba(255,255,255,.5);
          border-bottom: 2px solid transparent;
          transition: color .2s, border-color .2s;
        }
        .ft-widget__tab--active { color: #d68bff; border-bottom-color: #b32fc9; }
        .ft-widget__tab:hover:not(.ft-widget__tab--active) { color: rgba(255,255,255,.8); }

        .ft-widget__body {
          padding: 22px; overflow-y: auto; flex: 1;
        }

        .ft-widget__wa { text-align: center; padding: 8px 4px 4px; }
        .ft-widget__wa-icon {
          width: 76px; height: 76px; border-radius: 50%;
          background: #25d366; color: #fff;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 18px;
          box-shadow: 0 0 0 8px rgba(37,211,102,.12);
        }
        .ft-widget__wa-title { font-size: 16px; font-weight: 800; color: #fff; margin-bottom: 12px; }
        .ft-widget__wa-desc {
          font-size: 13px; line-height: 1.65; color: rgba(255,255,255,.6);
          margin-bottom: 22px;
        }
        .ft-widget__wa-btn {
          display: flex; align-items: center; justify-content: center; gap: 9px;
          width: 100%; background: #25d366; color: #fff;
          font-size: 14.5px; font-weight: 800;
          padding: 14px; border-radius: 8px;
          transition: background .2s, transform .15s;
        }
        .ft-widget__wa-btn:hover { background: #1eb858; transform: translateY(-2px); }
        .ft-widget__wa-unavailable { font-size: 13px; color: rgba(255,255,255,.45); }

        .ft-widget__form { display: flex; flex-direction: column; gap: 14px; }
        .ft-widget__honeypot { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
        .ft-widget__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .ft-widget__field { display: flex; flex-direction: column; gap: 6px; }
        .ft-widget__field label {
          font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase;
          color: rgba(255,255,255,.55);
        }
        .ft-widget__field input,
        .ft-widget__field select,
        .ft-widget__field textarea {
          font-family: 'Roboto', sans-serif; font-size: 13.5px; color: #fff;
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.12);
          border-radius: 7px; padding: 10px 12px;
          transition: border-color .2s, background .2s;
          resize: vertical;
        }
        .ft-widget__field input::placeholder,
        .ft-widget__field textarea::placeholder { color: rgba(255,255,255,.3); }
        .ft-widget__field select {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M2 4.5l5 5 5-5' stroke='%23ffffff' stroke-opacity='0.5' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 12px center;
          padding-right: 32px; cursor: pointer;
        }
        .ft-widget__field select option { background: #14132b; color: #fff; }
        .ft-widget__field input:focus,
        .ft-widget__field select:focus,
        .ft-widget__field textarea:focus {
          outline: none; border-color: #b32fc9; background: rgba(255,255,255,.1);
        }
        .ft-widget__input--error { border-color: #ff6464 !important; }
        .ft-widget__error { font-size: 11px; color: #ff8a8a; }
        .ft-widget__status {
          padding: 10px 12px; border-radius: 7px; font-size: 12.5px; font-weight: 600;
        }
        .ft-widget__status--error { background: rgba(255,100,100,.12); color: #ff9a9a; }

        .ft-widget__submit {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          background: linear-gradient(120deg, #6a1fc4 0%, #b32fc9 100%);
          color: #fff; border: none; cursor: pointer;
          font-size: 14px; font-weight: 800; letter-spacing: .02em;
          padding: 13px; border-radius: 8px;
          transition: opacity .2s, transform .15s;
        }
        .ft-widget__submit:hover:not(:disabled) { transform: translateY(-2px); }
        .ft-widget__submit:disabled { opacity: .6; cursor: not-allowed; }

        .ft-widget__success { text-align: center; padding: 16px 4px; }
        .ft-widget__success-icon {
          width: 56px; height: 56px; border-radius: 50%;
          background: #1a8a4c; display: flex; align-items: center; justify-content: center;
          margin: 0 auto 18px;
        }
        .ft-widget__success-msg {
          font-size: 13.5px; line-height: 1.7; color: rgba(255,255,255,.8); margin-bottom: 20px;
        }
        .ft-widget__success-btn {
          background: rgba(255,255,255,.08); color: #fff;
          border: 1px solid rgba(255,255,255,.16); cursor: pointer;
          font-size: 13px; font-weight: 700;
          padding: 10px 20px; border-radius: 7px;
          transition: background .2s;
        }
        .ft-widget__success-btn:hover { background: rgba(255,255,255,.16); }

        @media (max-width: 1024px) {
          .ft__col--brand { flex-basis: 100%; }
          .ft__about { max-width: 100%; }
        }
        @media (max-width: 640px) {
          .ft__band-inner { flex-direction: column; align-items: flex-start; }
          .ft__newsletter { max-width: 100%; }
          .ft__newsletter-row { flex-direction: column; border-radius: 8px; }
          .ft__newsletter-btn { justify-content: center; }
          .ft__inner { flex-direction: column; gap: 36px; padding: 0 20px; }
          .ft__col, .ft__col--brand, .ft__col:last-child { flex-basis: 100%; min-width: 0; }
          .ft__body { padding: 48px 0 36px; }
          .ft__band { padding: 50px 0; background-attachment: scroll; }
          .ft__band-inner { padding: 0 20px; }
          .ft__bottom-inner { flex-direction: column; align-items: flex-start; padding: 0 20px; }

          .ft__inner { align-items: center; text-align: center; }
          .ft__col, .ft__col--brand { align-items: center; display: flex; flex-direction: column; }
          .ft__about { text-align: center; max-width: 100%; }
          .ft__social { justify-content: center; }
          .ft__links { align-items: center; }
          .ft__link { justify-content: center; }
          .ft__col-heading { width: 100%; text-align: center; }
          .ft__col-heading::after { left: 50%; transform: translateX(-50%); }
          .ft__contact { align-items: center; }
          .ft__contact-item { flex-direction: row; align-items: center; text-align: left; justify-content: center; }
          .ft__bottom-inner { align-items: center; text-align: center; }
          .ft__bottom-left { align-items: center; }
          .ft__bottom-links { justify-content: center; }

          .ft-widget { right: 14px; bottom: 14px; }
          .ft-widget__fab { padding: 13px 20px; font-size: 13.5px; }
          .ft-widget__panel { width: calc(100vw - 28px); max-height: calc(100vh - 100px); }
          .ft-widget__row { grid-template-columns: 1fr; }
        }

        a.ft__link:focus-visible,
        a.ft__soc-link:focus-visible,
        a.ft__contact-value:focus-visible,
        a.ft__bottom-link:focus-visible,
        .ft__newsletter-input:focus-visible,
        .ft__newsletter-btn:focus-visible,
        .ft-widget__fab:focus-visible,
        .ft-widget__close:focus-visible,
        .ft-widget__tab:focus-visible,
        .ft-widget__submit:focus-visible,
        a.ft-widget__wa-btn:focus-visible {
          outline: 2px solid #029add;
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          .ft *, .ft *::before, .ft *::after { transition-duration: 0ms !important; }
          .ft-widget__panel { animation: none; }
        }
      `}</style>
    </footer>
  );
}

export default Footer;